import { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  EmptyState,
  EmptyStateTitle,
  IconBrowse,
} from '@spotify-internal/encore-web';
import { ApprovalStatus } from '@hooks/useAlbumInfo/types';
import { useGetProfileProtectionData } from '@hooks/useGetProfileProtectionData';
import { usePagination } from '@hooks/usePagination';
import { useProfileProtectionsCopy } from '@hooks/useProfileProtectionsCopy';
import { Loading } from '@components/shared/Loading';
import { ProfileProtectionsTable } from './ProfileProtectionsTable';
import { ApprovalStatusFilter } from './ApprovalStatusFilter';

const StyledEmptyState = styled(EmptyState)`
  min-height: 400px;
`;

const PaginationContainer = styled.div`
  position: relative;
`;

export const ProfileProtections = () => {
  const { data, isLoading } = useGetProfileProtectionData();
  const { page, limit, renderPagination, setPage } = usePagination();
  const copy = useProfileProtectionsCopy();
  const [selectedStatuses, setSelectedStatuses] = useState<ApprovalStatus[]>(
    [],
  );

  const handleFilterApply = useCallback(
    (statuses: ApprovalStatus[]) => {
      setSelectedStatuses(statuses);
      setPage(1); // Reset so user doesn't land on an empty page after filtering
    },
    [setPage],
  );

  if (isLoading) {
    return <Loading />;
  }

  if (data.length === 0) {
    return (
      <StyledEmptyState
        icon={IconBrowse}
        variant="contextual"
        maxInlineSize="400px"
        iconColorSet="brightAccent"
      >
        <EmptyStateTitle>{copy.noReleasesFound}</EmptyStateTitle>
      </StyledEmptyState>
    );
  }

  const filteredData =
    selectedStatuses.length > 0
      ? data.filter(item => selectedStatuses.includes(item.approvalStatus!))
      : data;
  const pagedData = filteredData.slice((page - 1) * limit, page * limit);

  return (
    <>
      <ApprovalStatusFilter
        selectedStatuses={selectedStatuses}
        onApply={handleFilterApply}
      />
      <ProfileProtectionsTable rows={pagedData} />
      <PaginationContainer>
        {renderPagination({
          totalResultRows: filteredData.length,
          finalTotalResultIsKnown: true,
          customRowsPerPageText: copy.resultsPerPage,
        })}
      </PaginationContainer>
    </>
  );
};
